/* eslint-disable no-console */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import Skeleton from '@mui/material/Skeleton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { useUser } from '../../../hooks/useUser';

import {
  setCart,
  setSelectedMenuItem,
  addUpdatingCartItems,
  setCartItems,
  setMinimumOrder,
} from '../../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import useTotalCartPrice from '../../../util/hooks/UseCart';
import IncDecBtnGroup from '../IncDecBtnGroup';
import { formatCartForApi } from '../../../util/Helpers';
import AddToCartPanel from '../../layout/AddToCartPanel/AddToCartPanel';
import { getTimeParams, countMenuItemPrice } from '../../../util/Restaurant';
import useClasses from '../../../hooks/legacy/useClasses';

import { ReactComponent as CouponSvg } from '../../../assets/icons/coupon.svg';
import cartImage from '../../../assets/images/emptyCartPlate.svg';

import styles from './CheckoutCartFormComponent.style';

const TOTAL_COST_NAME = 'Total';

function CheckoutCartFormComponent({ onAdjustedValue, onCheckoutUpdating }) {
  const [couponString, setCouponString] = useState('');
  const [couponFocus, setCouponFocus] = useState(false);
  const [filterCheckoutData, setFilterCheckoutData] = useState([]);
  const [tipValue, setTipValue] = useState(null);
  const [selectedTipPercentage, setSelectedTipPercentage] = useState(null);
  const [couponApplied, setCouponApplied] = useState(false);
  const [activeCoupon, setActiveCoupon] = useState(null);
  const classes = useClasses(styles);
  const dispatch = useDispatch();
  const location = useLocation();
  const cartData = useTotalCartPrice();
  const { subtotalPrice } = cartData;
  const { userData } = useUser();

  const {
    cartItems,
    orderType,
    updatingCartItems,
    dynamicFeesValues,
    minimums,
    checkoutData,
    selectedRestaurant,
    time,
    address,
  } = useSelector(
    ({ order, restaurants, address: selectedAddress, time: selectedTime }) => ({
      cartItems: order.cartItems,
      orderType: order.type,
      updatingCartItems: order.updatingCartItems,
      dynamicFeesValues: order.dynamicFeesValues,
      minimums: order.minimums,
      checkoutData: order.checkoutData,
      address: selectedAddress.selectedAddress,
      time: selectedTime.selectedTime,
      selectedRestaurant: restaurants.selectedRestaurant,
    })
  );
  const feesArray = (checkoutData && checkoutData?.fees) || [];

  const minOrderPrice = useMemo(
    () => minimums?.[orderType.value] || 0,
    [minimums, orderType]
  );

  const isMinOrderPrice = useMemo(
    () => minOrderPrice <= subtotalPrice,
    [minOrderPrice, subtotalPrice]
  );

  const onFeeValueChange = (name, value, forceAmount = false) => {
    let targetFee =
      dynamicFeesValues && dynamicFeesValues.length > 0
        ? dynamicFeesValues.find((i) => i.id === name)
        : null;

    const otherFees =
      dynamicFeesValues && dynamicFeesValues.length > 0
        ? dynamicFeesValues.filter((i) => i.id !== name)
        : [];

    let feeAmount = 0;
    const feeDescriptor = feesArray.find((f) => f.id === name);

    if (feeDescriptor) {
      if (forceAmount === true) {
        feeAmount = parseFloat(value);
      } else if (feeDescriptor.type === 'percent') {
        feeAmount = feeDescriptor.base * (value * 0.01);
        feeAmount = parseFloat(feeAmount.toFixed(2));
        setSelectedTipPercentage(value);
      } else {
        feeAmount = parseFloat(value);
      }
    }

    if (targetFee) {
      targetFee.amount = feeAmount;
    } else {
      targetFee = { id: name, amount: feeAmount };
    }

    const newDynamicFeesValues = [...otherFees];
    newDynamicFeesValues.push(targetFee);

    if (onAdjustedValue) {
      onAdjustedValue('fees', newDynamicFeesValues);
    }
  };

  const applyCoupon = () => {
    setCouponApplied(true);
    if (onAdjustedValue) {
      onAdjustedValue('coupons', [{ code: couponString }]);
    }
  };

  const deleteCartItem = useCallback(
    (index) => {
      const cartItem = cartItems[index];
      const newCartItems = [...cartItems];
      newCartItems.splice(index, 1);
      const newCartData = formatCartForApi(newCartItems);

      const apiData = {
        client: selectedRestaurant,
        data: {
          mode: orderType.value,
          ...getTimeParams(time),
          cart: newCartData,
        },
      };

      if (orderType.value === 'delivery' && address && address?.address_id) {
        apiData.data.address = address.address_id;
      }

      dispatch(setCartItems(newCartItems));
      dispatch(addUpdatingCartItems(cartItem.sort));

      dispatch(
        setCart({
          payloadCartData: { cart: newCartItems, sort: cartItem.sort },
          apiData,
          userProfile: userData,
          isCheckoutStart: true,
          pathname: location.pathname === '/checkout',
          adjust: true,
        })
      );
    },
    [
      cartItems,
      dispatch,
      address,
      selectedRestaurant,
      time,
      orderType,
      userData,
      location,
    ]
  );

  const editCartItem = useCallback(
    (index) => {
      const cartItem = cartItems[index];
      dispatch(
        setSelectedMenuItem({
          ...cartItem,
          id: cartItem.menuItemId,
        })
      );
    },
    [cartItems, dispatch]
  );

  const onQuantityChange = useCallback(
    (index, quantity) => {
      const newCart = [...cartItems];
      const cartItem = newCart[index];
      newCart[index] = {
        ...cartItem,
        quantity,
      };
      const newCartData = formatCartForApi(newCart);
      const apiData = {
        client: selectedRestaurant,
        data: {
          mode: orderType.value,
          ...getTimeParams(time),
          cart: newCartData,
        },
      };

      if (orderType.value === 'delivery' && address && address?.address_id) {
        apiData.data.address = address.address_id;
      }

      dispatch(setCartItems(newCart));
      dispatch(addUpdatingCartItems(cartItem.sort));
      dispatch(
        setCart({
          payloadCartData: { cart: newCart, sort: cartItem.sort },
          apiData,
          userProfile: userData,
          isCheckoutStart: true,
          pathname: location.pathname === '/checkout',
          adjust: true,
        })
      );
    },
    [
      dispatch,
      address,
      selectedRestaurant,
      time,
      orderType,
      cartItems,
      userData,
      location,
    ]
  );

  const handleRemoveCoupon = () => {
    setCouponString('');
    setCouponApplied(false);
    if (onAdjustedValue) {
      onAdjustedValue('coupons', []);
    }
  };

  useEffect(() => {
    if (!isMinOrderPrice) {
      dispatch(setMinimumOrder(true));
    } else {
      dispatch(setMinimumOrder(false));
    }
  }, [isMinOrderPrice]);

  const oldPrice = useMemo(() => {
    if (checkoutData) {
      if (checkoutData?.coupons?.length) {
        if (checkoutData?.coupons[0]?.discount) {
          return (
            checkoutData.total + checkoutData.coupons[0].discount
          ).toFixed(2);
        }

        return checkoutData.total;
      }
    }

    return 0;
  }, [checkoutData]);

  useEffect(() => {
    if (checkoutData) {
      const result = cartItems
        .filter((o1) =>
          checkoutData?.items.some((o2) => {
            if (!_.isNil(o1.menuItemId) && o1.menuItemId > 0) {
              return o1.menuItemId === o2.id;
            }

            return o1.menuItemId === o2.id;
          })
        )
        .map((i) => ({
          ...i,
          price: checkoutData?.items.find((ci) => ci.id === i.menuItemId)
            ?.price,
        }));
      setFilterCheckoutData(result);

      if (checkoutData?.coupons.length <= 0) {
        setActiveCoupon(null);
      } else {
        setActiveCoupon(checkoutData.coupons[0]);
        setCouponString(checkoutData.coupons[0].code);
      }
    }
  }, [checkoutData, cartItems]);

  useEffect(() => {
    if (feesArray) {
      const adjustableFee = feesArray.find((i) => i.adjustable === true);
      if (adjustableFee) {
        setSelectedTipPercentage(adjustableFee.percent);
        setTipValue(adjustableFee.amount);
      }
    }
  }, [feesArray]);

  const handleChangeCoupon = (value) => {
    setCouponApplied(false);
    setCouponString(value);
  };

  const taxesAndFees = useMemo(() => {
    const extraFeesTotal = feesArray
      .filter(
        (fee) => fee.is_delivery_fee === false && fee.adjustable === false
      )
      .reduce((sum, fee) => sum + fee.amount, 0);
    const totalTaxes = checkoutData?.taxes.reduce((acc, tax) => {
      if (tax) {
        return (
          acc +
          tax.targets.reduce((partial, target) => partial + target.total, 0)
        );
      }

      return 0;
    }, 0);
    return extraFeesTotal + totalTaxes;
  }, [checkoutData, feesArray]);

  const removedExtraFeesArray = useMemo(
    () =>
      feesArray.filter(
        (fee) => fee.is_delivery_fee === true || fee.adjustable === true
      ),
    [feesArray]
  );

  if (!cartItems.length) {
    return (
      <Box className={classes.emptyCart}>
        <Stack
          my={3}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <img
            src={cartImage}
            alt="Logo"
            className={classes.imageWidth}
            data-test-id="menu-header-image"
          />
          <Typography mt={2} color="text.secondary">
            Your cart is empty.
          </Typography>
          <Typography color="text.secondary">
            Add items to start a new order.
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      id="cart-form"
      data-test-id="checkout-cart-form"
      className={classes.parentBoxPadding}
    >
      {cartItems.length && Boolean(!isMinOrderPrice && minOrderPrice) ? (
        <Typography className={classes.minimumOrder}>
          {`Minimum order is $${minOrderPrice}`}
        </Typography>
      ) : null}
      <List className={classes.listItemWrapper} disablePadding>
        {checkoutData ? (
          filterCheckoutData.map((checkoutItem, cartItemIndex) => {
            const cartItem = cartItems.find(
              ({ menuItemId }) => menuItemId === checkoutItem.menuItemId
            );

            const itemPrice = countMenuItemPrice({
              orderType,
              menuItem: cartItem,
              quantity: checkoutItem.quantity,
              selectedOptions: checkoutItem.answers,
            });

            const price = checkoutItem.price.toFixed(2) || itemPrice.toFixed(2);
            const answers = [...(checkoutItem?.answers ?? [])]
              .sort((a, b) =>
                a.order > b.order || (a.order === b.order && a.sort > b.sort)
                  ? 1
                  : -1
              )
              .map((item) =>
                `${item.quantity}x ${item?.text}`.replace(/^1x /, '')
              );
            const answersText = answers.join(', ');
            const isUpdating = updatingCartItems.includes(checkoutItem.sort);

            const sortedAnswersInstructions = [...(checkoutItem?.answers ?? [])]
              .sort((a, b) =>
                a.order > b.order || (a.order === b.order && a.sort > b.sort)
                  ? 1
                  : -1
              )
              .map((item) => item.specialInstructions);

            return (
              <ListItem
                className={classes.listItem}
                key={`item-${checkoutItem.menuItemId}-${checkoutItem.sort}`}
                data-test-id="cart-item-wrapper"
              >
                <Box className={classes.restName}>
                  {`${checkoutItem?.quantity}x ${checkoutItem?.name}`}
                </Box>

                <Typography className={classes.cartItems}>
                  {answersText}
                </Typography>
                {sortedAnswersInstructions?.join('').length > 0 && (
                  <ul>
                    <li className={classes.specialCommentText}>
                      {sortedAnswersInstructions.join(' ')}
                    </li>
                  </ul>
                )}
                {Boolean(checkoutItem.comment) && (
                  <Box className={classes.comment}>
                    <Typography className={classes.commentText}>
                      Note: {checkoutItem?.comment}
                    </Typography>
                  </Box>
                )}
                <Box className={classes.cartItemPanel}>
                  <Stack direction="row" spacing={2}>
                    <IconButton
                      size="small"
                      variant="contained"
                      data-test-id="remove-item"
                      onClick={() => deleteCartItem(cartItemIndex)}
                      disabled={isUpdating || onCheckoutUpdating}
                      mr={1}
                      className={classes.iconColor}
                    >
                      <FontAwesomeIcon color="black" icon={faTrash} />
                    </IconButton>
                    <IconButton
                      variant="contained"
                      size="small"
                      data-test-id="edit-item"
                      onClick={() => editCartItem(cartItemIndex)}
                      disabled={isUpdating || onCheckoutUpdating}
                      ml={1}
                      className={classes.iconColor}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </IconButton>
                  </Stack>

                  <Box className={classes.incDecreaseCountPanel}>
                    <Typography
                      className={classes.price}
                    >{`$${price}`}</Typography>
                    {isUpdating && <CircularProgress size={24} />}

                    {!isUpdating && (
                      <IncDecBtnGroup
                        min={cartItems[checkoutItem.menuItemId]?.min || 1}
                        value={checkoutItem.quantity}
                        onChange={(value) =>
                          onQuantityChange(cartItemIndex, value)
                        }
                        updateInProcess={isUpdating || onCheckoutUpdating}
                        data-test-id="item-qty-change"
                      />
                    )}
                  </Box>
                </Box>
              </ListItem>
            );
          })
        ) : (
          <Skeleton
            height="5rem"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            }}
          />
        )}
      </List>

      {checkoutData ? (
        <Box className={classes.orderPriceDetails}>
          <Box key="subtotal.fee" className={classes.orderDetailsItem}>
            <Typography className={classes.priceTitle}>Subtotal </Typography>

            <Typography className={classes.priceNumber}>
              {`$${(checkoutData && checkoutData?.subtotal.toFixed(2)) || 0}`}
            </Typography>
          </Box>
          <Box key="total.taxes" className={classes.orderDetailsItem}>
            <Typography className={classes.priceTitle}>
              Taxes and Fees
            </Typography>
            <Typography className={classes.priceNumber}>{`$${Number(
              taxesAndFees
            ).toFixed(2)}`}</Typography>
          </Box>
          {checkoutData?.gift_certificate?.amount > 0 && (
            <Box key="total.gift" className={classes.orderDetailsItem}>
              <Typography
                className={`${classes.priceTitle} ${classes.accountBalanceColor}`}
              >
                Account Balance
              </Typography>
              <Typography
                className={`${classes.priceNumber} ${classes.accountBalanceColor}`}
              >{`-$${checkoutData.gift_certificate.amount.toFixed(
                2
              )}`}</Typography>
            </Box>
          )}
          {checkoutData &&
            removedExtraFeesArray.map((fee, index) => {
              if (fee.adjustable) {
                const baseValue = checkoutData?.fees?.find(
                  (f) => f.id === fee.id
                )?.base;
                if (!baseValue) return null;
                const firstThreeTips = fee.adjustable_percentages?.slice(0, 3);
                const lastThreeTips = fee.adjustable_percentages?.slice(3, 6);

                return (
                  <Box
                    className={classes.tips}
                    key={`fee-${fee.id || index}`}
                    data-test-id="fee-element"
                  >
                    <Box className={classes.mainTipBox}>
                      <Box className={classes.tipParentBox}>
                        <Typography
                          className={`${classes.priceTitle} ${classes.tipFeeName}`}
                          data-test-id="fee-name"
                        >
                          {fee.name}
                        </Typography>
                      </Box>
                      <Box
                        mt={2}
                        mr={1}
                        data-test-id="fee-value"
                        className={classes.tipValueColor}
                      >
                        ${Number(tipValue).toFixed(2)}
                      </Box>
                    </Box>
                    <Grid lg={12} sx={{ display: 'flex', ml: '19px' }}>
                      {firstThreeTips?.map((tipItem) => {
                        const isTipActive = tipItem === selectedTipPercentage;
                        const tipButtons =
                          tipItem === 0 ? 'No Tip' : `${tipItem}%`;

                        return (
                          <Grid
                            lg={6}
                            className={
                              isTipActive
                                ? classes.activeTipColor
                                : classes.gridColors
                            }
                            disabled={onCheckoutUpdating}
                            onClick={() => onFeeValueChange(fee.id, tipItem)}
                            data-test-id="tip-button"
                          >
                            <Box>{`${tipButtons}`}</Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Grid lg={12} sx={{ display: 'flex', ml: '19px' }}>
                      {lastThreeTips?.map((tipItem) => {
                        const isTipActive = tipItem === selectedTipPercentage;
                        return (
                          <Grid
                            lg={6}
                            className={
                              isTipActive
                                ? classes.activeTipColor
                                : classes.gridColors
                            }
                            data-test-id="tip-button"
                            disabled={onCheckoutUpdating}
                            onClick={() => onFeeValueChange(fee.id, tipItem)}
                          >
                            <Box>{`${tipItem}%`}</Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Box className={classes.tipTitle}>
                      Your driver is working hard to deliver your food. Show
                      them your gratitude. 100% of the tip goes to your driver.
                    </Box>
                  </Box>
                );
              }
              return (
                <Box
                  key={fee.id}
                  className={[
                    classes.orderDetailsItem,
                    fee.name === TOTAL_COST_NAME ? classes.total : '',
                  ].join(' ')}
                  data-test-id="fee-element"
                >
                  <Typography
                    className={classes.priceTitle}
                    data-test-id="fee-name"
                  >{`${fee.name}`}</Typography>

                  <Typography
                    className={classes.priceNumber}
                    data-test-id="fee-value"
                  >
                    {`$${(fee.value || fee.amount)?.toFixed(2)}`}
                  </Typography>
                </Box>
              );
            })}
          {activeCoupon && (
            <Box
              key="dicount.coupon"
              className={[classes.orderDetailsItem, classes.coupon].join(' ')}
            >
              <Typography className={classes.priceTitle}>
                Coupon discount{' '}
              </Typography>

              <Typography className={classes.priceNumber}>
                {`- $${
                  (checkoutData &&
                    checkoutData?.coupons[0]?.discount.toFixed(2)) ||
                  0
                }`}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box className={classes.orderPriceDetails}>
          <Skeleton
            height="7rem"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            }}
          />
        </Box>
      )}

      {activeCoupon ? (
        <Box className={classes.activeCoupon}>
          <CouponSvg className={classes.couponIcon} />
          <Box className={classes.couponInfo}>
            <Box>
              <Typography className={classes.couponTitle}>
                {activeCoupon.code}
              </Typography>
              <Typography className={classes.label}>
                You saved ${activeCoupon.discount.toFixed(2)} on your order.
              </Typography>
            </Box>
            <Button
              size="small"
              fullWidth
              variant="contained"
              className={clsx('btn-icon', classes.removeCoupon)}
              data-test-id="remove-coupon"
              disabled={onCheckoutUpdating}
              onClick={() => handleRemoveCoupon()}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </Box>
        </Box>
      ) : (
        <Box className={classes.giftCode}>
          <Box className={classes.redeemColor}>Redeem a Coupon</Box>
          <Box className={classes.couponInputWidth}>
            <TextField
              InputProps={{
                className: clsx({
                  [classes.couponField]: true,
                  [classes.invalidCounponInput]:
                    couponApplied &&
                    !onCheckoutUpdating &&
                    !activeCoupon &&
                    couponString.length > 0,
                }),
                'data-test-id': 'coupon-input',
              }}
              variant="outlined"
              value={couponString}
              onChange={(e) => handleChangeCoupon(e?.target?.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
              onFocus={() => setCouponFocus(true)}
              onBlur={() => setCouponFocus(false)}
              fullWidth
              placeholder="Enter coupon code"
              data-test-id="200-off-monday"
              disabled={onCheckoutUpdating}
            />

            <Button
              className={clsx({
                [classes.giftCodeSubmitBtn]: true,
                [classes.invalidCounponBtn]:
                  couponApplied &&
                  !onCheckoutUpdating &&
                  !activeCoupon &&
                  couponString.length > 0,
                [classes.btnFocused]: couponFocus,
              })}
              variant="contained"
              data-test-id="giftcode-submit"
              onClick={applyCoupon}
              disabled={activeCoupon || onCheckoutUpdating}
            >
              Apply
            </Button>
          </Box>

          {couponApplied &&
            !onCheckoutUpdating &&
            !activeCoupon &&
            couponString.length > 0 && (
              <Typography className={classes.invalidCounpon}>
                Coupon code is not valid
              </Typography>
            )}
        </Box>
      )}
      <Box
        key="total.fee"
        className={[classes.orderDetailsItem, classes.total].join(' ')}
      >
        <Typography
          className={classes.priceTitle}
          data-test-id="checkout-total-fee-element"
        >
          Total{' '}
        </Typography>
        <Typography
          className={`${classes.priceNumber} ${classes.totalPrice}`}
          data-test-id="checkout-total-value"
        >
          {activeCoupon && (
            <Typography className={classes.oldPrice}>
              {`$${(checkoutData && oldPrice) || 0}`}
            </Typography>
          )}
          {`$${(checkoutData && checkoutData?.total.toFixed(2)) || 0}`}
        </Typography>
      </Box>
      <AddToCartPanel />
    </Box>
  );
}

CheckoutCartFormComponent.propTypes = {
  onAdjustedValue: PropTypes.func,
  onCheckoutUpdating: PropTypes.bool.isRequired,
};

CheckoutCartFormComponent.defaultProps = {
  onAdjustedValue: null,
};

export default CheckoutCartFormComponent;
